import * as React from 'react';
import { Typography, FormControl, FormHelperText, TextField, MenuItem, Select, InputLabel, Grid, Box, Button } from '@mui/material';
import { FormContext } from '../../context/FormContext';
import { OrderContext } from '../../context/OrderContext';
import { CompanyContext } from '../../context/CompanyContext';
import { useEvent } from '../../context/EventContext';
import { useFormik } from 'formik';
import { differenceInDays, isValid, parseISO, startOfDay } from 'date-fns';
import * as yup from 'yup';
import { isPlanAvailable } from '../../utils/plan';

export default function TypeSubcriber() {
  const { activeStep, setActiveStep } = React.useContext(FormContext);
  const { company } = React.useContext(CompanyContext);
  const { order, setOrder } = React.useContext(OrderContext);
  const { events } = useEvent();
  const paymentOptions = ['Boleto', 'Pix', 'Transação ou Depósito Bancário']
  const [isFree, setIsFree] = React.useState(false)
  const [isHosting, setIsHosting] = React.useState(false)
  const [isDaylis, setIsDaylis] = React.useState(false)
  const [minQty, setMinQty] = React.useState(1);
  const [maxQty, setMaxQty] = React.useState(15);
  const today = startOfDay(new Date());


  const orderValidation = yup.object({
    plan: yup
      .number()
      .min(1, 'Nenhum tipo de inscrição selecionado')
      .required('Campo Necessário'),
    plan_describe: yup
      .string(),
    plan_price: yup
      .string(),
    quantity: yup
      .number()
      .positive('Quantidade deve ser positiva')
      .min(minQty, `${minQty} é o mínimo de inscrições`)
      .max(maxQty, `${maxQty} é o máximo de inscrições`)
      .test('duplo-mult-2', 'Para hospedagem dupla, a quantidade deve ser múltipla de 2', function (value) {
        const { hosting } = this.parent;
        const selectedHost = events.hostings?.find(h => String(h.id) === String(hosting));
        if (selectedHost && selectedHost.type?.toLowerCase() === 'duplo') {
          return value % 2 === 0;
        }
        return true;
      })
      .test('patrocinador-quotas', function (value) {
        if (events.isControlCompany && company?.affiliate?.type === 'PATROCINADOR') {
          const quotasDisponiveis = company.affiliate.quotas || 0;

          if (quotasDisponiveis === 0) {
            return this.createError({
              message: 'Suas quotas de patrocinador foram esgotadas. Entre em contato com a organização do evento.'
            });
          }

          if (value > quotasDisponiveis) {
            return this.createError({
              message: `Quantidade excede o limite de quotas disponíveis. Você possui ${quotasDisponiveis} quota(s) restante(s).`
            });
          }
        }
        return true;
      })
      .required('Quantidade necessária'),
    payment_form: yup
      .string()
      .test('is-free', 'Forma de pagamento necessária', function (value) {
        const { path, createError } = this;
        if (isFree) {
          return true;
        }
        if (!value) {
          return createError({ path, message: 'Forma de pagamento necessária' });
        }
        return true;
      })
      .required('Tipo de pagamento necessário'),
    hosting: yup
      .string()
      .test('is-host', 'Hospedagem necessária', function (value) {
        const { path, createError } = this;
        if (!isHosting) {
          return true;
        }
        if (!value) {
          return createError({ path, message: 'Hospedagem necessária' });
        }
        return true;
      })
      .required('Hospedagem necessária'),
    daily: yup
      .number()
      .positive('Diárias deve ser positiva')
      .min(0, '0 é o mínimo de diárias')
      .max(30, '30 é o máximo de diárias')
      .required('Diárias necessárias'),
    checkin: yup
      .string()
      .when([], {
        is: () => isHosting,
        then: schema => schema.required('Data de check-in obrigatória')
          .test('checkin-min-today', 'Check-in deve ser hoje ou depois', value => {
            if (!value) return false;
            const checkinDate = parseISO(value);
            return isValid(checkinDate) && !checkinDate < today;
          })
          .test('checkin-before-checkout', 'Check-in deve ser antes do check-out', function (value) {
            const { checkout } = this.parent;
            if (!value || !checkout) return true;
            const checkinDate = parseISO(value);
            const checkoutDate = parseISO(checkout);
            return isValid(checkinDate) && isValid(checkoutDate) && checkinDate < checkoutDate;
          }),
        otherwise: schema => schema.notRequired()
      }),
    checkout: yup
      .string()
      .when([], {
        is: () => isHosting,
        then: schema => schema.required('Data de check-out obrigatória')
          .test('checkout-after-checkin', 'Check-out deve ser após o check-in', function (value) {
            const { checkin } = this.parent;
            if (!value || !checkin) return true;
            const checkinDate = parseISO(checkin);
            const checkoutDate = parseISO(value);
            return isValid(checkinDate) && isValid(checkoutDate) && checkoutDate > checkinDate;
          }),
        otherwise: schema => schema.notRequired()
      }),

  })

  const typeValidation = useFormik({
    initialValues: {
      quantity: order.quantity || 0,
      plan: order.plan || "",
      plan_describe: order.plan_describe || "",
      plan_price: order.plan_price || "",
      payment_form: "",
      hosting: order.hosting || "NoHosting",
      pricebyday: order.pricebyday || 0,
      daily: order.daily || 0,
      checkin: order.checkin || "",
      checkout: order.checkout || "",

    },
    validationSchema: orderValidation,
    onSubmit: values => {
      setOrder({ ...order, ...values })
      setActiveStep(activeStep + 1)
    },
    validateOnMount: true,
    enableReinitialize: true
  })
  // Atualiza min/max inscritos conforme hospedagem e quotas de patrocinador
  React.useEffect(() => {
    const selectedHost = events.hosting?.find(h => h.id === typeValidation.values.hosting);
    let min = 1;
    let max = 15;

    // Verifica se é PATROCINADOR com controle de quotas ativo
    if (events.isControlCompany && company?.affiliate?.type === 'PATROCINADOR') {
      const quotasDisponiveis = company.affiliate.quotas || 0;

      if (quotasDisponiveis === 0) {
        // Se quotas zeradas, não permite inscrições
        max = 0;
        min = 0;
      } else {
        // Limita o máximo ao número de quotas disponíveis
        max = quotasDisponiveis;
        min = 1;
      }
    }

    // Ajusta conforme tipo de hospedagem
    if (selectedHost) {
      if (selectedHost.type?.toLowerCase() === 'duplo') {
        min = Math.max(2, min);
        max = Math.min(14, max);
        // Se tiver quotas ímpar, ajusta para o par imediatamente inferior
        if (max % 2 !== 0 && max > 0) {
          max = max - 1;
        }
        if (typeValidation.values.quantity % 2 !== 0) {
          typeValidation.setFieldValue('quantity', min);
        }
      }
    }

    setMinQty(min);
    setMaxQty(max);

    // Ajusta quantidade atual se ultrapassar novo limite
    if (typeValidation.values.quantity > max) {
      typeValidation.setFieldValue('quantity', max);
    }
    if (typeValidation.values.quantity < min) {
      typeValidation.setFieldValue('quantity', min);
    }
  }, [typeValidation.values.hosting, events.hostings, events.isControlCompany, company?.affiliate]);

  // Calcula diárias ao alterar checkin/checkout
  React.useEffect(() => {
    if (typeValidation.values.checkout && typeValidation.values.checkin) {
      const start = parseISO(typeValidation.values.checkin);
      const end = parseISO(typeValidation.values.checkout);
      if (isValid(start) && isValid(end)) {
        const days = differenceInDays(end, start);
        const diarias = days > 0 ? days : 0;
        setIsDaylis(diarias);
        typeValidation.setFieldValue('daily', diarias);
      }
    }
  }, [typeValidation.values.checkin, typeValidation.values.checkout]);

  React.useEffect(() => {
    setIsFree(typeValidation.values.plan_price == 0)
  }, [typeValidation.values.plan_price])

  const filteredPlans = React.useMemo(() => {
    if (!events.plans) {
      return events.plans || [];
    }

    const normalizeType = (value = '') =>
      value
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toLowerCase();

    const isStaffPlan = (planType) => planType === 'staff';

    // Se a empresa não tem tipo de afiliação, mostra os planos que não são para associados
    if (!company?.affiliate?.type || !company?.affiliate?.active) {
      return events.plans.filter(plan => {
        if (!isPlanAvailable(plan)) return false;
        if (!plan.type) return true;
        const planType = normalizeType(plan.type);

        if (isStaffPlan(planType)) {
          return true;
        }

        // Exclui planos específicos para RPPS associados
        return !planType.includes('rpps associado') &&
          !planType.includes('patrocinador');
      });
    }

    const affiliateType = company.affiliate.type;

    const blockedTypeRaw = affiliateType === 'PATROCINADOR' ? company?.quotaControl?.isBlockedType : null;
    const blockedTypes = Array.isArray(blockedTypeRaw)
      ? blockedTypeRaw
      : (blockedTypeRaw || '').split(',');
    const normalizedBlockedTypes = blockedTypes
      .map(type => normalizeType(type))
      .filter(Boolean);

    return events.plans.filter(plan => {
      if (!isPlanAvailable(plan)) return false;

      if (!plan.type) return true;

      const planType = normalizeType(plan.type);

      if (isStaffPlan(planType)) {
        return true;
      }

      // Planos gratuitos sempre visíveis quando isControlCompany e empresa tem afiliação
      if (events.isControlCompany && company?.affiliate && plan.price == 0) {
        return true;
      }

      const isRppsAssociadoBlocked = normalizedBlockedTypes.includes('rpps associado');

      // Mapeamento dos tipos de afiliação para os tipos de planos
      switch (affiliateType) {
        case 'RPPS_ASSOCIADO':
          if (isRppsAssociadoBlocked) {
            return planType.includes('rpps nao associado'); // Bloqueia planos de RPPS Associado se o bloqueio estiver ativo
          }
          return planType.includes('rpps associado');

        case 'RPPS_OUTROS_ESTADOS':
          return planType.includes('rpps de outros estados') || planType.includes('rpps outros estados');

        case 'PATROCINADOR':
          return planType.includes('patrocinador');

        default:
          return !planType.includes('rpps associado') &&
            !planType.includes('patrocinador');
      }
    });
  }, [events.plans, company?.affiliate?.type, company?.affiliate?.active, company?.quotaControl?.isBlockedType]);


  return (
    <React.Fragment>
      <Typography variant="h6" gutterBottom>
        Tipo e Quantidade de Inscrições
      </Typography>
      <form onSubmit={typeValidation.handleSubmit}>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={isHosting ? 3 : 5} md={isHosting ? 3 : 5}>
            <FormControl fullWidth>
              <InputLabel id="plan">Tipo de Inscrição</InputLabel>
              <Select
                labelId="plan"
                //defaultValue={typeValidation.values.plan}
                //value={typeValidation.values.plan}
                label="Tipo de Inscrição"
                error={typeValidation.touched.plan && typeValidation.errors.plan}
                helperText={typeValidation.touched.plan && typeValidation.errors.plan}
                onChange={(e, name) => {
                  typeValidation.setFieldValue('plan', e.target.value)
                  name.props.price == 0 ? typeValidation.setFieldValue('payment_form', 'Gratuito') && setIsFree(true) : typeValidation.setFieldValue('payment_form', 'Boleto') && setIsFree(false)
                  typeValidation.setFieldValue('plan_describe', name.props.name)
                  typeValidation.setFieldValue('plan_price', name.props.price)
                }}
              >

                {
                  !events.isControlCompany && events.plans.filter(plan => isPlanAvailable(plan)).map(plan => <MenuItem value={plan.id} name={plan.type} price={plan.price}>{plan.type} - R${plan.price}</MenuItem>)
                }
                {
                  events.isControlCompany && filteredPlans.map((plan, index) => (
                    <MenuItem
                      key={plan.id}
                      value={plan.id}
                      name={plan.type}
                      price={plan.price}
                    >
                      {plan.type} - R${plan.price}
                    </MenuItem>
                  ))
                }
              </Select>
              <FormHelperText error>{typeValidation.touched.plan && typeValidation.errors.plan ? typeValidation.errors.plan : null}</FormHelperText>
            </FormControl>
          </Grid>
          {events.hostings.length > 0 &&
            <Grid item xs={12} sm={2}>
              <FormControl fullWidth>
                <InputLabel id="hosting">Hospedagem</InputLabel>
                <Select
                  name='hosting'
                  id="hosting"
                  labelId="hosting"
                  label="Hospedagem"
                  fullWidth
                  value={typeValidation.values.hosting}
                  onChange={(e, name) => {
                    typeValidation.setFieldValue('hosting', e.target.value)
                    e.target.value === 'NoHosting' ? setIsHosting(false) : setIsHosting(true)
                    typeValidation.setFieldValue('pricebyday', name.props.price)
                  }}
                  error={typeValidation.touched.hosting && Boolean(typeValidation.errors.hosting)}
                  helperText={typeValidation.touched.hosting && typeValidation.errors.hosting}
                >
                  <MenuItem value={'NoHosting'}>Sem Hospedagem</MenuItem>
                  {events.hostings &&
                    events.hostings.map((host) => {
                      if (host.status) return (
                        <MenuItem value={host.id} price={host.pricebyday}>{`${host.name} ${host.type} ${Intl.NumberFormat('pt-br', { style: 'currency', currency: 'BRL' }).format(host.pricebyday)} `}</MenuItem>
                      )
                    })
                  }
                </Select>
                <FormHelperText error>{typeValidation.touched.hosting && typeValidation.errors.hosting ? typeValidation.errors.hosting : null}</FormHelperText>
              </FormControl>
            </Grid>
          }
          {isHosting &&
            <>
              <Grid item xs={12} sm={2}>
                <TextField
                  label="Check-in"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  name="checkin"
                  value={typeValidation.values.checkin}
                  onChange={typeValidation.handleChange}
                  error={typeValidation.touched.checkin && Boolean(typeValidation.errors.checkin)}
                  helperText={typeValidation.touched.checkin && typeValidation.errors.checkin}
                  inputProps={{ min: today.toISOString().split('T')[0] }}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  label="Check-out"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  name="checkout"
                  value={typeValidation.values.checkout}
                  onChange={typeValidation.handleChange}
                  error={typeValidation.touched.checkout && Boolean(typeValidation.errors.checkout)}
                  helperText={typeValidation.touched.checkout && typeValidation.errors.checkout}
                  inputProps={{ min: typeValidation.values.checkin ? typeValidation.values.checkin : today.toISOString().split('T')[0] }}
                />
              </Grid>
              <Grid item xs={12} sm={1}>
                <TextField
                  label="Diárias"
                  value={isDaylis || 0}
                  InputProps={{ readOnly: true }}
                  fullWidth
                />
              </Grid>
            </>
          }
          <Grid item xs={12} sm={isHosting ? 1 : 2} display={isFree ? 'none' : 'flex'} >
            <FormControl fullWidth>
              <InputLabel id="payment_form">Forma de Pagamento</InputLabel>
              <Select
                name='payment_form'
                id="payment_form"
                labelId="payment_form"
                label="Forma de Pagamento"
                fullWidth
                value={typeValidation.values.payment_form}
                onChange={typeValidation.handleChange}
                error={typeValidation.touched.payment_form && Boolean(typeValidation.errors.payment_form)}
                helperText={typeValidation.touched.payment_form && typeValidation.errors.payment_form}
              >
                <MenuItem disabled value={"Gratuito"} sx={{ display: isFree ? 'none' : 'none' }}>Gratuito</MenuItem>
                {!!events.payment_options &&
                  Object.keys(events.payment_options).map((form) => {
                    if (events.payment_options[form]) {
                      return (
                        <MenuItem value={form} sx={{ display: events.payment_options[form] ? 'block' : 'none' }} >{form}</MenuItem>
                      )
                    }
                  })
                }
                {!events.payment_options &&
                  paymentOptions.map((form) => {
                    return (
                      <MenuItem value={form}>{form}</MenuItem>
                    )
                  })
                }
              </Select>
              <FormHelperText error>{typeValidation.touched.payment_form && typeValidation.errors.payment_form ? typeValidation.errors.payment_form : null}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={isHosting ? 1 : 2} md={isHosting ? 1 : 2}>
            <TextField
              name="quantity"
              label="Quantidade de inscrições"
              fullWidth
              type={'number'}
              variant="outlined"
              id="quantity"
              value={typeValidation.values.quantity}
              onChange={typeValidation.handleChange}
              error={typeValidation.touched.quantity && Boolean(typeValidation.errors.quantity)}
              helperText={
                typeValidation.touched.quantity && typeValidation.errors.quantity
                  ? typeValidation.errors.quantity
                  : (events.isControlCompany && company?.affiliate?.type === 'PATROCINADOR'
                    ? `Quotas disponíveis: ${company.affiliate.quotas || 0}`
                    : null)
              }
              inputProps={{ min: minQty, max: maxQty }}
              disabled={maxQty === 0}
            />
          </Grid>
        </Grid>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            type={'button'}
            onClick={() => setActiveStep(activeStep - 1)}
            sx={{ mt: 3, ml: 1 }}>
            Voltar
          </Button>

          <Button
            variant="contained"
            type={'submit'}
            sx={{ mt: 3, ml: 1 }}
          >
            Próximo
          </Button>
        </Box>
      </form>
    </React.Fragment>
  );
}
